import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import '../../assets/styles/HelpModal.css';
import { useLanguage } from '../../assets/js/LanguageContext';
import SectionEyebrow from '../common/SectionEyebrow';

import Tutorial1 from '../../assets/images/Tutorial/1.png';
import Tutorial2 from '../../assets/images/Tutorial/2.png';
import Tutorial3 from '../../assets/images/Tutorial/3.png';
import Tutorial5 from '../../assets/images/Tutorial/5.png';
import Tutorial6 from '../../assets/images/Tutorial/6.png';
import Tutorial7 from '../../assets/images/Tutorial/7.png';
import Tutorial8 from '../../assets/images/Tutorial/8.png';
import Tutorial9 from '../../assets/images/Tutorial/9.png';
import Tutorial10 from '../../assets/images/Tutorial/10.png';
import Tutorial11 from '../../assets/images/Tutorial/11.png';
import Tutorial12 from '../../assets/images/Tutorial/12.png';
import Tutorial13 from '../../assets/images/Tutorial/13.png';

const HELP_IMAGE_FALLBACK =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlbiBubyBkaXNwb25pYmxlPC90ZXh0Pjwvc3ZnPg==';

const helpImages = [
  {
    id: 1,
    src: Tutorial1,
    alt: 'Menu principal con funcionalidades',
    title: 'Menu principal',
    description:
      'El menu con las funcionalidades: empezar con una plantilla, construir un diagnostico, procesar archivos con informacion, y empezar con un prompt'
  },
  {
    id: 2,
    src: Tutorial2,
    alt: 'Empezar con una plantilla',
    title: 'Plantillas disponibles',
    description:
      'Empezar con una plantilla donde podemos ver las plantillas existentes: clima laboral, perfil sociolaboral y cultura de equipo'
  },
  {
    id: 3,
    src: Tutorial3,
    alt: 'Construir un diagnostico',
    title: 'Construir diagnostico',
    description: 'Otra vez el menu principal pero ahora nos enfocamos en construir un diagnostico'
  },
  {
    id: 5,
    src: Tutorial5,
    alt: 'Configurar preguntas',
    title: 'Configurar preguntas',
    description: 'Donde configuramos las preguntas del diagnostico'
  },
  {
    id: 6,
    src: Tutorial6,
    alt: 'Tipos de preguntas',
    title: 'Tipos de preguntas',
    description: 'Que tipos de preguntas podemos crear: abiertas, cerradas y de rating'
  },
  {
    id: 7,
    src: Tutorial7,
    alt: 'Vista previa de preguntas',
    title: 'Vista previa',
    description:
      'Una vista previa de las preguntas que hemos creado y podemos ver tambien el boton de generar PIN y guardar'
  },
  {
    id: 8,
    src: Tutorial8,
    alt: 'Generar PIN',
    title: 'Generar PIN',
    description:
      'Lo que pasa despues de pulsar el boton de generar PIN y que nos aparece un texto sugerido para compartir por WhatsApp o Teams'
  },
  {
    id: 9,
    src: Tutorial9,
    alt: 'Dashboard con diagnostico creado',
    title: 'Dashboard',
    description: 'Vemos el dashboard y vemos que se ha creado el diagnostico'
  },
  {
    id: 10,
    src: Tutorial10,
    alt: 'Procesar archivos',
    title: 'Procesar archivos',
    description: 'Volvemos a ver el menu pero ahora nos enfocamos en procesar archivos'
  },
  {
    id: 11,
    src: Tutorial11,
    alt: 'Subir archivos',
    title: 'Subir archivos',
    description:
      'El apartado de subir archivos donde pone "arrastra y suelta tu archivo aqui" y acepta Excel'
  },
  {
    id: 12,
    src: Tutorial12,
    alt: 'Empezar con prompt',
    title: 'Empezar con prompt',
    description: 'Otra vez vemos el menu principal y nos enfocamos a empezar con un prompt'
  },
  {
    id: 13,
    src: Tutorial13,
    alt: 'Crear diagnostico con prompt',
    title: 'Diagnostico con prompt',
    description: 'Como se crean diagnosticos a partir de un prompt'
  }
];

const HelpModal = ({ isOpen, onClose, showOnFirstVisit = false }) => {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = helpImages.length;
  const currentHelpImageBase = helpImages[currentSlide];
  const currentHelpImage = {
    ...currentHelpImageBase,
    alt: t(`help_slide_${currentHelpImageBase.id}_alt`),
    title: t(`help_slide_${currentHelpImageBase.id}_title`),
    description: t(`help_slide_${currentHelpImageBase.id}_description`),
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 5000);

    return () => clearInterval(interval);
  }, [isOpen, totalSlides]);

  useEffect(() => {
    if (showOnFirstVisit && !localStorage.getItem('helpShown')) {
      localStorage.setItem('helpShown', 'true');
    }
  }, [showOnFirstVisit]);

  if (!isOpen) return null;

  return (
    <div className="help-modal-overlay" onClick={onClose}>
      <div className="help-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="help-modal-header">
          <div className="help-modal-heading">
            <SectionEyebrow>{t('help_eyebrow')}</SectionEyebrow>
            <h2>{t('help_title')}</h2>
          </div>
          <button
            type="button"
            className="help-close-button"
            onClick={onClose}
            aria-label={t('help_close_aria')}
          >
            <X size={18} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>

        <div className="help-slider-container">
          <button
            type="button"
            className="help-nav-button help-prev"
            onClick={prevSlide}
            aria-label={t('help_prev_aria')}
          >
            <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
          </button>

          <article className="help-slide" key={currentHelpImage.id}>
            <div className="help-slide-content">
              <div className="help-image-container">
                <img
                  src={currentHelpImage.src}
                  alt={currentHelpImage.alt}
                  className="help-slide-image"
                  draggable="false"
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  onError={(e) => {
                    e.target.src = HELP_IMAGE_FALLBACK;
                  }}
                />
              </div>

              <div className="help-slide-text">
                <div className="help-slide-copy">
                  <span className="help-step-badge">
                    {t('help_step')} <strong>{String(currentSlide + 1).padStart(2, '0')}</strong>
                  </span>
                  <h3>{currentHelpImage.title}</h3>
                  <p>{currentHelpImage.description}</p>
                </div>

                <div className="help-dots" aria-label={t('help_dots_aria')}>
                  {helpImages.map((image, index) => (
                    <button
                      key={image.id}
                      type="button"
                      className={`help-dot ${index === currentSlide ? 'active' : ''}`}
                      onClick={() => goToSlide(index)}
                      aria-label={`${t('help_go_step')} ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </article>

          <button
            type="button"
            className="help-nav-button help-next"
            onClick={nextSlide}
            aria-label={t('help_next_aria')}
          >
            <ChevronRight size={20} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>

        <div className="help-modal-footer">
          <div className="help-progress">
            <span className="help-progress-bar" aria-hidden="true">
              <span
                className="help-progress-fill"
                style={{ width: `${((currentSlide + 1) / totalSlides) * 100}%` }}
              />
            </span>
            <span>{currentSlide + 1}</span>
            <span className="help-separator">/</span>
            <span>{totalSlides}</span>
          </div>
          <button type="button" className="btn btn--primary help-got-it-button" onClick={onClose}>
            {t('common_understood')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
